import { Params } from '@angular/router';
import { BasketItemSource } from '@epicuro-next/models/basket';
import { selectOpenedListId } from '@epicuro-next/state/list-entities';
import { selectProductBasketSearchQuery } from '@epicuro-next/state/p-basket-state';
import { selectQueryParams } from '@epicuro-next/state/router-store';
import {
  getDeliveryShopId,
  getValue,
  isEqual,
  removeEmptyValues,
  stringifyObjectValues,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { pipe } from 'rxjs';

import { DeliveryItemsByShop, DeliveryPeriodsByShop } from '../../models';
import { backgroundProductsAdapter } from '../../reducers/config-strip-business.reducer';
import { selectConfigStripState } from '../config-strip.feature.selector';

export const selectBusinessConfigStripState = createSelector(
  selectConfigStripState,
  (state) => state?.business,
);

export const selectBackgroundProductsState = createSelector(
  selectBusinessConfigStripState,
  (business) => business.backgroundProducts,
);

const { selectAll } = backgroundProductsAdapter.getSelectors(
  selectBackgroundProductsState,
);

export const selectBackgroundProducts = selectAll;

/**
 * selects if the CS currently persisting data
 */
export const selectPersistingChanges = createSelector(
  selectBusinessConfigStripState,
  (business) => business.persistingChanges,
);

export const selectDeliveryItems = createSelector(
  selectBusinessConfigStripState,
  (business) => business.deliveryItems,
);

/* map quantity value with reducing usedTrueDemandQuantity */
export const selectDeliveryItemsWithSuppliedQuantity = createSelector(
  selectDeliveryItems,
  (deliveryItems) => {
    return Object.fromEntries(
      Object.entries(deliveryItems).map(([key, deliveryItemByPeriod]) => [
        key,
        Object.fromEntries(
          Object.entries(deliveryItemByPeriod).map(
            ([periodKey, deliveryItem]) => [
              periodKey,
              {
                orderType: deliveryItem.orderType,
                deliveryPeriodType: deliveryItem.deliveryPeriodType,
                items: Object.fromEntries(
                  Object.entries(deliveryItem.items).map(
                    ([productId, item]) => [
                      productId,
                      {
                        ...item,
                        quantity: item.quantity - item.usedTrueDemandQuantity,
                      },
                    ],
                  ),
                ),
              },
            ],
          ),
        ),
      ]),
    );
  },
);

export const selectTotalQuantity = createSelector(
  selectBusinessConfigStripState,
  (business) => business.totalQuantity,
);

export const selectTotalPrices = createSelector(
  selectBusinessConfigStripState,
  (business) => business.totalPrices,
);

export const selectCurrentlyActiveDeliveryShop = createSelector(
  selectBusinessConfigStripState,
  (state) => state?.activeDeliveryShop,
);

export const selectCurrentlyActiveDeliveryShopKey = createSelector(
  selectCurrentlyActiveDeliveryShop,
  getDeliveryShopId,
);

export const selectModifiedProducts = createSelector(
  selectBusinessConfigStripState,
  (business) => business.modifiedProducts ?? {},
);

/** Select delivery periods interface */
export const selectDeliveryPeriods = createSelector(
  selectBusinessConfigStripState,
  (business) => business.deliveryPeriods,
);

export const selectNowDeliveryPeriods = createSelector(
  selectBusinessConfigStripState,
  (business) => business.nowDeliveryPeriods,
);

export const selectCachedState = createSelector(
  selectBusinessConfigStripState,
  (business) => business.cached ?? {},
);

export const selectProductSource = createSelector(
  selectBusinessConfigStripState,
  (business) => business?.source,
);

export const possibleListIdPasses = [
  'details.recommendationPanel.correlationId',
  'details.listId',
  'additionalUiDetails.listId',
];

export const selectSetDefaultListSourceNeeded = createSelector(
  selectProductSource,
  selectOpenedListId,
  (source, openedListId) => {
    if (!openedListId) return false;
    if (!source?.type) return true;

    const isListIdInSource = possibleListIdPasses
      .flatMap((pass) => getValue(source, pass))
      .includes(openedListId);

    return !isListIdInSource;
  },
);

export const selectSetDefaultSearchSourceNeeded = createSelector(
  selectProductBasketSearchQuery,
  selectProductSource,
  selectQueryParams,
  (search, currentSource, params) => {
    if (currentSource?.type === BasketItemSource.SEARCH_SUGGEST_FILTER) {
      // reset source if:
      // 1. search in not empty as suggest click reset search input
      if (search) return true;

      // 2. search params are not equal if filters changed
      return !isBasketSourceParamsEquals(
        params,
        currentSource.additionalUiDetails?.searchParams ?? {},
      );
    }

    const isQueryEquals =
      !!currentSource?.additionalUiDetails?.query &&
      `${currentSource.additionalUiDetails.query}` === search;
    // compare filters when query is empty when User redirected from landing page recommendation to search url
    const isParamsEquals =
      !currentSource?.additionalUiDetails?.query &&
      !search &&
      !!currentSource?.additionalUiDetails?.searchParams &&
      isEqual(
        // needs to stringify values before future comparison (ex. num, start, collapse are number or boolean)
        stringifyObjectValues(currentSource.additionalUiDetails.searchParams),
        params,
      );
    return !isQueryEquals && !isParamsEquals;
  },
);

export const selectShowNextButton = createSelector(
  selectBusinessConfigStripState,
  (business) => business.showNextButton,
);

export const selectDirty = createSelector(
  selectBusinessConfigStripState,
  (business) => business.dirty,
);

export const selectUsedInventories = createSelector(
  selectBusinessConfigStripState,
  (business) => {
    return business.usedInventories;
  },
);

export const selectIsSetSourceNeed = createSelector(
  selectBusinessConfigStripState,
  (business) => {
    return business.isSetSourceNeed;
  },
);

export const selectDeliveryItemsAndPeriods = createSelector(
  selectDeliveryItems,
  selectDeliveryPeriods,
  selectNowDeliveryPeriods,
  (
    deliveryItems: DeliveryItemsByShop,
    deliveryPeriods: DeliveryPeriodsByShop,
    nowDeliveryPeriods: DeliveryPeriodsByShop,
  ) => ({ deliveryItems, deliveryPeriods, nowDeliveryPeriods }),
);

function extractBasketSourceParamsToCompare(params: Params = {}) {
  const { q, withRelatedVariants, num, start, ...paramsToCompare } = params;
  return paramsToCompare;
}

function isBasketSourceParamsEquals(
  params1: Params = {},
  params2: Params = {},
) {
  return isEqual(
    pipe(
      extractBasketSourceParamsToCompare,
      stringifyObjectValues,
      removeEmptyValues,
    )(params1),
    pipe(
      extractBasketSourceParamsToCompare,
      stringifyObjectValues,
      removeEmptyValues,
    )(params2),
  );
}
